/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors'
import { styles } from '../posShared.styles';
import type { PosPaymentMethodId, PosPaymentMethodRow } from '../../domain/posTypes';

type PaymentMethodChipRowProps = {
  methods: PosPaymentMethodRow[];
  selectedId: PosPaymentMethodId;
  onSelect: (methodId: PosPaymentMethodId) => void;
  disabled?: boolean;
  /** Retorna true para métodos deshabilitados (ej. efectivo sin caja abierta). */
  isMethodLocked?: (methodId: string) => boolean;
  /** Nota bajo los chips cuando hay algún método bloqueado (explica el candado). */
  lockedHint?: string;
};

const METHOD_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  cash: 'cash-outline',
  transfer: 'swap-horizontal-outline',
  card: 'card-outline',
  mercadopago_qr: 'qr-code-outline',
  cheque: 'document-text-outline',
  fiado: 'book-outline',
};

function methodIcon(methodId: string): keyof typeof Ionicons.glyphMap {
  return METHOD_ICONS[methodId] ?? 'wallet-outline';
}

// Todos los medios visibles de una: los chips envuelven en filas (2 por fila en
// narrow/wide) en vez de un scroll horizontal que escondía opciones.
export function PaymentMethodChipRow({
  methods,
  selectedId,
  onSelect,
  disabled,
  isMethodLocked,
  lockedHint,
}: PaymentMethodChipRowProps) {
  if (methods.length === 0) {
    return <Text style={styles.caption}>No hay métodos configurados.</Text>;
  }

  const anyLocked = methods.some((method) => isMethodLocked?.(method.id));

  return (
    <View>
      <View style={chipStyles.row}>
        {methods.map((method) => {
          const locked = isMethodLocked?.(method.id) ?? false;
          const methodDisabled = disabled || locked;
          const active = method.id === selectedId;
          return (
            <TouchableOpacity
              key={method.id}
              style={[chipStyles.cell, active && chipStyles.cellActive, locked && chipStyles.cellLocked]}
              onPress={() => !methodDisabled && onSelect(method.id as PosPaymentMethodId)}
              disabled={methodDisabled}
              accessibilityRole="button"
              accessibilityLabel={`Pago ${method.name}`}
              accessibilityState={{ selected: active, disabled: methodDisabled }}
              testID={`pos.checkout.payment-method.${method.id}`}
            >
              <Ionicons
                name={methodIcon(method.id)}
                size={15}
                color={active ? ticketColors.gold : ticketColors.ink2}
              />
              <Text
                style={[chipStyles.label, active && chipStyles.labelActive]}
                numberOfLines={1}
              >
                {method.name}
              </Text>
              {locked ? (
                <Ionicons name="lock-closed-outline" size={12} color={ticketColors.ink3} />
              ) : null}
            </TouchableOpacity>
          );
        })}
      </View>
      {anyLocked && lockedHint ? (
        <Text style={chipStyles.lockedNote}>{lockedHint}</Text>
      ) : null}
    </View>
  );
}

const chipStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingVertical: 2,
  },
  cell: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    flexGrow: 1,
    flexBasis: '45%',
    minHeight: 40,
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    backgroundColor: ticketColors.tile,
  },
  cellActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  cellLocked: {
    opacity: 0.55,
  },
  lockedNote: {
    fontSize: 11,
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
    marginTop: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  labelActive: {
    color: ticketColors.gold,
  },
});
